import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Plus, Trash2, Briefcase, Search, Globe, Link2, RefreshCw, EyeOff, QrCode, ChevronRight, ChevronDown, Users, ArrowLeft, Save } from 'lucide-react';
import clsx from 'clsx';
import toast from 'react-hot-toast';
import { createPosition, updatePosition, deletePosition } from '../api';
import { Position, User } from '../types';

// Searchable select dropdown
function SearchableSelect({ value, options, placeholder, onChange, onAdd, className }: {
  value: string;
  options: { label: string; value: string }[];
  placeholder?: string;
  onChange: (v: string) => void;
  onAdd?: () => void;
  className?: string;
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const ref = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const filtered = query
    ? options.filter(o => o.label.toLowerCase().includes(query.toLowerCase()))
    : options;

  const handleClickOutside = useCallback((e: MouseEvent) => {
    if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
  }, []);

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [handleClickOutside]);

  useEffect(() => { if (open) { setQuery(''); inputRef.current?.focus(); } }, [open]);

  const displayLabel = options.find(o => o.value === value)?.label || value || placeholder || '选择...';

  return (
    <div ref={ref} className={clsx('relative', className)}>
      <button type="button" onClick={() => setOpen(!open)}
        className="w-full h-9 px-3 rounded-lg border border-gray-200 text-sm bg-white focus:outline-none focus:ring-1 focus:ring-primary-500 text-left truncate flex items-center justify-between gap-1">
        <span className={clsx('truncate', !value && 'text-gray-400')}>{displayLabel}</span>
        <ChevronRight size={12} className={clsx('shrink-0 text-gray-400 transition-transform', open && 'rotate-90')} />
      </button>
      {open && (
        <div className="absolute z-50 mt-1 w-full min-w-[200px] bg-white rounded-lg border border-gray-200 shadow-lg max-h-60 flex flex-col">
          <div className="p-1.5 border-b border-gray-100">
            <div className="relative">
              <Search size={12} className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-400" />
              <input ref={inputRef} value={query} onChange={e => setQuery(e.target.value)}
                placeholder="搜索部门..."
                className="w-full pl-7 pr-2 py-1.5 text-xs rounded border border-gray-100 focus:outline-none focus:ring-1 focus:ring-primary-500" />
            </div>
          </div>
          <div className="overflow-y-auto flex-1">
            <button type="button" onClick={() => { onChange(''); setOpen(false); }}
              className={clsx('w-full text-left px-3 py-1.5 text-xs hover:bg-gray-50', !value && 'bg-primary-50 text-primary-600')}>
              未分配
            </button>
            {filtered.map(o => (
              <button key={o.value} type="button" onClick={() => { onChange(o.value); setOpen(false); }}
                className={clsx('w-full text-left px-3 py-1.5 text-xs hover:bg-gray-50 truncate', value === o.value && 'bg-primary-50 text-primary-600 font-medium')}>
                {o.label}
              </button>
            ))}
            {filtered.length === 0 && <div className="px-3 py-2 text-xs text-gray-400">无匹配结果</div>}
          </div>
          {onAdd && (
            <button type="button" onClick={() => { setOpen(false); onAdd(); }}
              className="w-full text-left px-3 py-1.5 text-xs text-primary-600 hover:bg-primary-50 border-t border-gray-100 font-medium">
              + 新建部门...
            </button>
          )}
        </div>
      )}
    </div>
  );
}

interface Props {
  positions: Position[];
  onUpdate: () => void;
  currentUser: User;
  notificationBell?: React.ReactNode;
}

const PRIORITY_OPTIONS = [
  { value: '', label: '无', color: '' },
  { value: 'P0', label: 'P0 紧急', color: 'bg-red-100 text-red-700 border-red-200' },
  { value: 'P1', label: 'P1 重要', color: 'bg-orange-100 text-orange-700 border-orange-200' },
  { value: 'P2', label: 'P2 一般', color: 'bg-blue-100 text-blue-700 border-blue-200' },
  { value: 'reserve', label: '长期储备', color: 'bg-gray-100 text-gray-600 border-gray-300' },
];

const FUNNEL_STAGES = [
  { key: 'new', label: '待筛选', color: 'bg-gray-400' },
  { key: 'screening', label: '筛选中', color: 'bg-blue-400' },
  { key: 'screening_passed', label: '筛选通过', color: 'bg-blue-500' },
  { key: 'interview_scheduled', label: '面试中', color: 'bg-indigo-500' },
  { key: 'interview_completed', label: '面试完成', color: 'bg-purple-500' },
  { key: 'offer', label: 'Offer', color: 'bg-green-500' },
  { key: 'onboarded', label: '已入职', color: 'bg-green-600' },
];

interface DeptOption { id: string; full_name: string; parent_name: string | null; }

export default function PositionManager({ positions, onUpdate, currentUser, notificationBell }: Props) {
  const [newName, setNewName] = useState('');
  const [newDept, setNewDept] = useState('');
  const [newDesc, setNewDesc] = useState('');
  const [deptOptions, setDeptOptions] = useState<DeptOption[]>([]);
  const [search, setSearch] = useState('');
  const [priorityFilter, setPriorityFilter] = useState('');
  const [statusTab, setStatusTab] = useState<'active' | 'closed'>('active');
  const isHr = currentUser.role === 'admin' || currentUser.role === 'hr' || currentUser.role === 'vip';

  // Detail view
  const [detailPos, setDetailPos] = useState<Position | null>(null);
  const [editForm, setEditForm] = useState<Partial<Position>>({});

  // Priority card expand state
  const [expandedPriorities, setExpandedPriorities] = useState<Set<string>>(new Set());
  const togglePriority = (key: string) => setExpandedPriorities(prev => {
    const next = new Set(prev);
    next.has(key) ? next.delete(key) : next.add(key);
    return next;
  });

  // Career portal settings
  const [careerToken, setCareerToken] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [showCareerPanel, setShowCareerPanel] = useState(false);

  const fetchDepartments = () => {
    fetch('/api/org/departments', { headers: { Authorization: `Bearer ${localStorage.getItem('ats_token')}` } })
      .then(r => r.json())
      .then(data => { if (Array.isArray(data)) setDeptOptions(data); })
      .catch(() => {});
  };

  useEffect(() => {
    if (isHr) {
      fetch('/api/positions/career-settings', {
        headers: { Authorization: `Bearer ${localStorage.getItem('ats_token')}` }
      })
        .then(r => r.json())
        .then(d => { setCareerToken(d.career_token || ''); setCompanyName(d.company_name || ''); })
        .catch(() => {});
      fetchDepartments();
    }
  }, [isHr]);

  const careerUrl = careerToken ? `${window.location.origin}/career/${careerToken}` : '';

  const handleSaveCareerSettings = async () => {
    try {
      const res = await fetch('/api/positions/career-settings', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('ats_token')}` },
        body: JSON.stringify({ company_name: companyName })
      });
      const data = await res.json();
      setCareerToken(data.career_token);
      toast.success('已保存');
    } catch { toast.error('保存失败'); }
  };

  const handleRegenerateToken = async () => {
    if (!window.confirm('重新生成链接后，旧链接将失效。确认？')) return;
    try {
      const res = await fetch('/api/positions/career-settings', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('ats_token')}` },
        body: JSON.stringify({ regenerate_token: true })
      });
      const data = await res.json();
      setCareerToken(data.career_token);
      toast.success('链接已更新');
    } catch { toast.error('失败'); }
  };

  const handleTogglePublic = async (p: Position, e?: React.MouseEvent) => {
    e?.stopPropagation();
    try {
      await updatePosition(p.id, { is_public: p.is_public ? 0 : 1 });
      toast.success(p.is_public ? '已取消公开' : '已公开');
      onUpdate();
    } catch { toast.error('更新失败'); }
  };

  const handleCreate = async () => {
    if (!newName.trim()) { toast.error('请输入岗位名称'); return; }
    try {
      await createPosition({ name: newName.trim(), department: newDept.trim(), description: newDesc.trim() });
      setNewName(''); setNewDept(''); setNewDesc('');
      toast.success('岗位已创建');
      onUpdate();
    } catch { toast.error('创建失败'); }
  };

  const handleDelete = async (id: string, name: string) => {
    if (!window.confirm(`确认删除岗位「${name}」？归属该岗位的候选人将变为未分配。`)) return;
    try {
      await deletePosition(id);
      toast.success('岗位已删除');
      setDetailPos(null);
      onUpdate();
    } catch { toast.error('删除失败'); }
  };

  const handleToggleStatus = async (p: Position, e?: React.MouseEvent) => {
    e?.stopPropagation();
    const newStatus = p.status === 'open' ? 'paused' : 'open';
    try {
      await updatePosition(p.id, { status: newStatus });
      toast.success(newStatus === 'open' ? '岗位已开启' : '岗位已暂停');
      onUpdate();
    } catch { toast.error('更新失败'); }
  };

  const handleSaveDetail = async () => {
    if (!detailPos) return;
    try {
      await updatePosition(detailPos.id, editForm);
      toast.success('岗位已更新');
      setDetailPos(null);
      onUpdate();
    } catch { toast.error('保存失败'); }
  };

  const openDetail = (p: Position) => {
    setDetailPos(p);
    setEditForm({
      name: p.name, department: p.department, description: p.description,
      priority: p.priority || '', background: p.background || '',
      location: p.location || '', salary_range: p.salary_range || '',
      requirements: p.requirements || '',
    });
  };

  const statusLabels: Record<string, string> = { open: '招聘中', paused: '已暂停', closed: '已关闭' };
  const statusColors: Record<string, string> = {
    open: 'bg-green-100 text-green-700',
    paused: 'bg-yellow-100 text-yellow-700',
    closed: 'bg-gray-100 text-gray-500',
  };

  const filtered = positions.filter(p => {
    // Status tab filter
    if (statusTab === 'active' && (p.status === 'closed' || p.status === 'paused')) return false;
    if (statusTab === 'closed' && p.status === 'open') return false;
    if (priorityFilter && p.priority !== priorityFilter) return false;
    if (search) {
      const q = search.toLowerCase();
      return p.name.toLowerCase().includes(q) || (p.department || '').toLowerCase().includes(q) || (p.background || '').toLowerCase().includes(q);
    }
    return true;
  });

  const openCount = positions.filter(p => p.status === 'open').length;
  const closedCount = positions.filter(p => p.status !== 'open').length;
  const activePositions = positions.filter(p => p.status === 'open');
  const priorityCounts: Record<string, number> = {
    '': activePositions.length,
    'P0': activePositions.filter(p => p.priority === 'P0').length,
    'P1': activePositions.filter(p => p.priority === 'P1').length,
    'P2': activePositions.filter(p => p.priority === 'P2').length,
    'reserve': activePositions.filter(p => p.priority === 'reserve').length,
  };
  const p0Count = priorityCounts['P0'];
  const p1Count = priorityCounts['P1'];

  // ----- DETAIL VIEW -----
  if (detailPos) {
    const totalFunnel = Object.values(detailPos.funnel || {}).reduce((a, b) => a + b, 0);
    return (
      <div className="h-full flex flex-col bg-gray-50">
        <div className="shrink-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button onClick={() => setDetailPos(null)} className="p-1.5 hover:bg-gray-100 rounded-lg transition-colors">
              <ArrowLeft size={18} className="text-gray-500" />
            </button>
            <div>
              <h1 className="text-lg font-bold text-gray-800">{detailPos.name}</h1>
              <p className="text-xs text-gray-500">{detailPos.department || '未分配部门'} · {statusLabels[detailPos.status]}</p>
            </div>
            {detailPos.priority && (
              <span className={clsx('px-2 py-0.5 rounded-full text-[10px] font-bold border', PRIORITY_OPTIONS.find(o => o.value === detailPos.priority)?.color)}>
                {PRIORITY_OPTIONS.find(o => o.value === detailPos.priority)?.label || detailPos.priority}
              </span>
            )}
          </div>
          {isHr && (
            <button onClick={handleSaveDetail} className="h-9 px-4 text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 rounded-lg flex items-center gap-1.5">
              <Save size={14} />保存修改
            </button>
          )}
        </div>

        <div className="flex-1 overflow-y-auto p-6">
          <div className="max-w-4xl mx-auto space-y-6">
            {/* Funnel */}
            <div className="bg-white rounded-xl border border-gray-200 p-5">
              <h3 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
                <Users size={15} className="text-primary-500" />
                招聘漏斗 · 共 {totalFunnel} 人
              </h3>
              {totalFunnel === 0 ? (
                <p className="text-sm text-gray-400">暂无候选人</p>
              ) : (
                <div className="space-y-2">
                  {FUNNEL_STAGES.map(s => {
                    const count = (detailPos.funnel || {})[s.key] || 0;
                    if (count === 0) return null;
                    const pct = Math.round((count / totalFunnel) * 100);
                    return (
                      <div key={s.key} className="flex items-center gap-3">
                        <span className="text-xs text-gray-500 w-16 shrink-0">{s.label}</span>
                        <div className="flex-1 h-5 bg-gray-100 rounded-full overflow-hidden">
                          <div className={clsx('h-full rounded-full transition-all', s.color)} style={{ width: `${Math.max(pct, 3)}%` }} />
                        </div>
                        <span className="text-xs font-medium text-gray-600 w-14 text-right">{count} 人 ({pct}%)</span>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Edit form */}
            {isHr && (
              <div className="bg-white rounded-xl border border-gray-200 p-5">
                <h3 className="text-sm font-semibold text-gray-700 mb-4">岗位信息</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-xs text-gray-500 mb-1 block">岗位名称</label>
                    <input value={editForm.name || ''} onChange={e => setEditForm({ ...editForm, name: e.target.value })}
                      className="w-full h-9 px-3 rounded-lg border border-gray-200 text-sm focus:outline-none focus:ring-1 focus:ring-primary-500" />
                  </div>
                  <div>
                    <label className="text-xs text-gray-500 mb-1 block">部门</label>
                    <SearchableSelect
                      value={editForm.department || ''}
                      options={[
                        ...deptOptions.map(d => ({ label: d.full_name, value: d.full_name })),
                        ...(editForm.department && !deptOptions.find(d => d.full_name === editForm.department)
                          ? [{ label: `${editForm.department}（未录入）`, value: editForm.department }] : []),
                      ]}
                      placeholder="未分配"
                      onChange={v => setEditForm({ ...editForm, department: v })}
                      onAdd={() => {
                        const dn = prompt('输入新部门名称（格式：父部门-子部门）');
                        if (dn?.trim()) {
                          const parts = dn.trim().split('-');
                          const nm = parts.length > 1 ? parts[parts.length - 1] : parts[0];
                          const pn = parts.length > 1 ? parts[0] : '';
                          const pr = pn ? deptOptions.find(d => d.full_name === pn || d.full_name.endsWith(pn)) : null;
                          fetch('/api/org/units', { method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('ats_token')}` },
                            body: JSON.stringify({ name: nm, type: 'team', parent_id: pr?.id || null }) })
                            .then(() => { fetchDepartments(); setEditForm({ ...editForm, department: dn.trim() }); toast.success('部门已创建'); })
                            .catch(() => toast.error('创建失败'));
                        }
                      }}
                    />
                  </div>
                  <div>
                    <label className="text-xs text-gray-500 mb-1 block">优先级</label>
                    <select value={editForm.priority || ''} onChange={e => setEditForm({ ...editForm, priority: e.target.value })}
                      className="w-full h-9 px-3 rounded-lg border border-gray-200 text-sm focus:outline-none focus:ring-1 focus:ring-primary-500">
                      {PRIORITY_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="text-xs text-gray-500 mb-1 block">工作地点</label>
                    <input value={editForm.location || ''} onChange={e => setEditForm({ ...editForm, location: e.target.value })}
                      className="w-full h-9 px-3 rounded-lg border border-gray-200 text-sm focus:outline-none focus:ring-1 focus:ring-primary-500" />
                  </div>
                  <div>
                    <label className="text-xs text-gray-500 mb-1 block">薪资范围</label>
                    <input value={editForm.salary_range || ''} onChange={e => setEditForm({ ...editForm, salary_range: e.target.value })} placeholder="如：15-25K"
                      className="w-full h-9 px-3 rounded-lg border border-gray-200 text-sm focus:outline-none focus:ring-1 focus:ring-primary-500" />
                  </div>
                  <div className="col-span-2">
                    <label className="text-xs text-gray-500 mb-1 block">招聘背景</label>
                    <textarea value={editForm.background || ''} onChange={e => setEditForm({ ...editForm, background: e.target.value })} rows={2} placeholder="为什么招这个岗位、团队情况等..."
                      className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm focus:outline-none focus:ring-1 focus:ring-primary-500 resize-none" />
                  </div>
                  <div className="col-span-2">
                    <label className="text-xs text-gray-500 mb-1 block">岗位描述</label>
                    <textarea value={editForm.description || ''} onChange={e => setEditForm({ ...editForm, description: e.target.value })} rows={3}
                      className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm focus:outline-none focus:ring-1 focus:ring-primary-500 resize-none" />
                  </div>
                  <div className="col-span-2">
                    <label className="text-xs text-gray-500 mb-1 block">任职要求</label>
                    <textarea value={editForm.requirements || ''} onChange={e => setEditForm({ ...editForm, requirements: e.target.value })} rows={3}
                      className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm focus:outline-none focus:ring-1 focus:ring-primary-500 resize-none" />
                  </div>
                </div>

                <div className="flex items-center justify-between mt-5 pt-4 border-t border-gray-100">
                  <div className="flex items-center gap-3">
                    <button onClick={() => handleTogglePublic(detailPos)}
                      className={clsx('px-3 py-1.5 rounded-lg text-xs font-medium flex items-center gap-1.5 border transition-all', detailPos.is_public ? 'text-indigo-600 bg-indigo-50 border-indigo-200' : 'text-gray-500 bg-gray-50 border-gray-200')}>
                      {detailPos.is_public ? <><Globe size={12} />公开中</> : <><EyeOff size={12} />未公开</>}
                    </button>
                    <button onClick={() => handleToggleStatus(detailPos)}
                      className="px-3 py-1.5 text-xs text-gray-500 hover:text-primary-600 border border-gray-200 rounded-lg">
                      {detailPos.status === 'open' ? '暂停招聘' : '开启招聘'}
                    </button>
                  </div>
                  <button onClick={() => handleDelete(detailPos.id, detailPos.name)}
                    className="px-3 py-1.5 text-xs text-red-500 hover:bg-red-50 border border-red-200 rounded-lg flex items-center gap-1">
                    <Trash2 size={12} />删除岗位
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  // ----- LIST VIEW -----
  return (
    <div className="h-full flex flex-col bg-gray-50">
      {/* Header */}
      <div className="shrink-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
        <div>
          <h1 className="text-lg font-bold text-gray-800 flex items-center gap-2">
            <Briefcase size={20} className="text-primary-500" />
            岗位管理
          </h1>
          <p className="text-xs text-gray-500 mt-0.5">
            共 {positions.length} 个岗位 · {openCount} 招聘中 · {closedCount} 已完结
            {p0Count > 0 && <span className="text-red-600 font-medium"> · P0×{p0Count}</span>}
            {p1Count > 0 && <span className="text-orange-600 font-medium"> · P1×{p1Count}</span>}
          </p>
        </div>
        {notificationBell}
      </div>

      <div className="flex-1 overflow-y-auto p-6">
        {/* Career Portal Settings */}
        {isHr && (
          <div className="bg-white rounded-xl border border-gray-200 p-5 mb-6">
            <div className="flex items-center justify-between">
              <h2 className="text-sm font-semibold text-gray-700 flex items-center gap-2">
                <Globe size={15} className="text-indigo-500" />
                招聘门户（公开收集简历）
              </h2>
              <button onClick={() => setShowCareerPanel(!showCareerPanel)}
                className="text-xs text-indigo-600 hover:text-indigo-800">
                {showCareerPanel ? '收起' : '展开设置'}
              </button>
            </div>

            {showCareerPanel && (
              <div className="mt-4 space-y-3">
                <div className="flex items-center gap-2">
                  <label className="text-xs text-gray-500 w-16 shrink-0">公司名称</label>
                  <input value={companyName} onChange={e => setCompanyName(e.target.value)} placeholder="展示在招聘页顶部"
                    className="h-8 px-3 rounded-lg border border-gray-200 text-sm flex-1 focus:outline-none focus:ring-1 focus:ring-indigo-500" />
                  <button onClick={handleSaveCareerSettings}
                    className="h-8 px-3 text-xs bg-indigo-600 text-white rounded-lg hover:bg-indigo-700">保存</button>
                </div>
                {careerUrl && (
                  <div className="flex items-center gap-2 bg-gray-50 rounded-lg p-3">
                    <Link2 size={14} className="text-gray-400 shrink-0" />
                    <input value={careerUrl} readOnly className="flex-1 text-xs text-gray-600 bg-transparent border-none outline-none" />
                    <button onClick={() => { navigator.clipboard.writeText(careerUrl); toast.success('链接已复制'); }}
                      className="text-xs text-indigo-600 hover:text-indigo-800 font-medium whitespace-nowrap">复制</button>
                    <button onClick={() => window.open(careerUrl, '_blank')}
                      className="text-xs text-indigo-600 hover:text-indigo-800 font-medium whitespace-nowrap">预览</button>
                    <button onClick={() => window.open(`https://api.qrserver.com/v1/create-qr-code/?size=300x300&data=${encodeURIComponent(careerUrl)}`, '_blank')}
                      className="text-xs text-indigo-600 hover:text-indigo-800 font-medium whitespace-nowrap flex items-center gap-0.5"><QrCode size={12} />二维码</button>
                  </div>
                )}
                <div className="flex items-center justify-between">
                  <p className="text-[11px] text-gray-400">将岗位设为「公开」后，会展示在招聘门户上供外部投递</p>
                  <button onClick={handleRegenerateToken} className="text-[11px] text-gray-400 hover:text-red-500 flex items-center gap-1">
                    <RefreshCw size={10} />重新生成链接
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Create new position */}
        {isHr && (
          <div className="bg-white rounded-xl border border-gray-200 p-5 mb-6">
            <h2 className="text-sm font-semibold text-gray-700 mb-3">创建新岗位</h2>
            <div className="flex flex-wrap gap-2">
              <input value={newName} onChange={e => setNewName(e.target.value)} placeholder="岗位名称 *"
                className="h-9 px-3 rounded-lg border border-gray-200 text-sm flex-1 min-w-[150px] focus:outline-none focus:ring-1 focus:ring-primary-500" />
              <SearchableSelect
                value={newDept}
                options={deptOptions.map(d => ({ label: d.full_name, value: d.full_name }))}
                placeholder="选择部门"
                onChange={v => setNewDept(v)}
                onAdd={() => {
                  const deptName = prompt('输入新部门名称（如需创建子部门，格式：父部门-子部门）');
                  if (deptName?.trim()) {
                    const parts = deptName.trim().split('-');
                    const name = parts.length > 1 ? parts[parts.length - 1] : parts[0];
                    const parentName = parts.length > 1 ? parts[0] : '';
                    const parent = parentName ? deptOptions.find(d => d.full_name === parentName || d.full_name.endsWith(parentName)) : null;
                    fetch('/api/org/units', {
                      method: 'POST',
                      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('ats_token')}` },
                      body: JSON.stringify({ name, type: 'team', parent_id: parent?.id || null }),
                    }).then(r => r.json()).then(() => { fetchDepartments(); setNewDept(deptName.trim()); toast.success('部门已创建'); })
                      .catch(() => toast.error('创建部门失败'));
                  }
                }}
                className="w-44"
              />
              <input value={newDesc} onChange={e => setNewDesc(e.target.value)} placeholder="岗位描述"
                className="h-9 px-3 rounded-lg border border-gray-200 text-sm flex-1 min-w-[150px] focus:outline-none focus:ring-1 focus:ring-primary-500" />
              <button onClick={handleCreate} disabled={!newName.trim()}
                className="h-9 px-4 text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 rounded-lg disabled:opacity-40 flex items-center gap-1.5 transition-colors">
                <Plus size={14} />创建
              </button>
            </div>
          </div>
        )}

        {/* Status tabs */}
        <div className="mb-4 flex items-center gap-4 border-b border-gray-200">
          <button onClick={() => setStatusTab('active')}
            className={clsx('pb-2 text-sm font-medium border-b-2 transition-colors', statusTab === 'active' ? 'text-primary-600 border-primary-600' : 'text-gray-400 border-transparent hover:text-gray-600')}>
            招聘中 ({openCount})
          </button>
          <button onClick={() => setStatusTab('closed')}
            className={clsx('pb-2 text-sm font-medium border-b-2 transition-colors', statusTab === 'closed' ? 'text-primary-600 border-primary-600' : 'text-gray-400 border-transparent hover:text-gray-600')}>
            已完结 ({closedCount})
          </button>
        </div>

        {/* Priority summary cards */}
        {statusTab === 'active' && activePositions.length > 0 && (() => {
          const cards = [
            {
              key: 'P0', label: 'P0 紧急', count: priorityCounts['P0'],
              accent: 'text-red-600', bg: 'bg-red-50', border: 'border-red-200', dot: 'bg-red-500',
              positions: activePositions.filter(p => p.priority === 'P0'),
            },
            {
              key: 'P1', label: 'P1 重要', count: priorityCounts['P1'],
              accent: 'text-orange-600', bg: 'bg-orange-50', border: 'border-orange-200', dot: 'bg-orange-400',
              positions: activePositions.filter(p => p.priority === 'P1'),
            },
            {
              key: 'P2', label: 'P2 一般', count: priorityCounts['P2'],
              accent: 'text-blue-600', bg: 'bg-blue-50', border: 'border-blue-200', dot: 'bg-blue-400',
              positions: activePositions.filter(p => p.priority === 'P2'),
            },
            {
              key: 'reserve', label: '长期储备', count: priorityCounts['reserve'],
              accent: 'text-gray-500', bg: 'bg-gray-50', border: 'border-gray-200', dot: 'bg-gray-400',
              positions: activePositions.filter(p => p.priority === 'reserve'),
            },
          ].filter(c => c.count > 0);
          return (
            <div className="mb-4 grid grid-cols-2 md:grid-cols-4 gap-3">
              {cards.map(card => {
                const isOpen = expandedPriorities.has(card.key);
                // Group by dept for expanded view
                const byDept: Record<string, string[]> = {};
                card.positions.forEach(p => {
                  const d = p.department || '未分配';
                  if (!byDept[d]) byDept[d] = [];
                  byDept[d].push(p.name);
                });
                return (
                  <div key={card.key} className={clsx('bg-white rounded-xl border transition-all', isOpen ? card.border : 'border-gray-200')}>
                    <button
                      onClick={() => togglePriority(card.key)}
                      className="w-full flex items-center justify-between px-4 py-3 text-left"
                    >
                      <div className="flex items-center gap-2.5">
                        <span className={clsx('w-2 h-2 rounded-full shrink-0', card.dot)} />
                        <div>
                          <div className={clsx('text-3xl font-bold leading-none', card.accent)}>{card.count}</div>
                          <div className="text-xs text-gray-500 mt-0.5">{card.label}</div>
                        </div>
                      </div>
                      <ChevronDown size={14} className={clsx('text-gray-400 transition-transform shrink-0', isOpen && 'rotate-180')} />
                    </button>
                    {isOpen && (
                      <div className={clsx('border-t px-4 py-3 space-y-2', card.border)}>
                        {Object.entries(byDept).map(([dept, names]) => (
                          <div key={dept}>
                            <div className="text-[10px] font-medium text-gray-400 mb-1">{dept}</div>
                            <div className="flex flex-col gap-0.5">
                              {names.map(name => (
                                <button
                                  key={name}
                                  onClick={() => { const p = activePositions.find(x => x.name === name); if (p) openDetail(p); }}
                                  className="text-xs text-left text-gray-700 hover:text-primary-600 truncate transition-colors"
                                >
                                  {name}
                                </button>
                              ))}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          );
        })()}

        {/* Search + priority filter */}
        <div className="mb-4 flex items-center gap-3">
          <div className="relative flex-1 max-w-sm">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input value={search} onChange={e => setSearch(e.target.value)} placeholder="搜索岗位名称、部门、招聘背景..."
              className="w-full h-9 pl-9 pr-3 rounded-lg border border-gray-200 text-sm bg-white focus:outline-none focus:ring-1 focus:ring-primary-500" />
          </div>
          {statusTab === 'active' && (
            <div className="flex items-center gap-1.5">
              {['', 'P0', 'P1', 'P2', 'reserve'].map(pv => {
                const opt = PRIORITY_OPTIONS.find(o => o.value === pv);
                const count = priorityCounts[pv] ?? 0;
                const isActive = priorityFilter === pv;
                return (
                  <button key={pv} onClick={() => setPriorityFilter(pv === priorityFilter ? '' : pv)}
                    className={clsx('px-3 py-2 rounded-lg text-xs font-medium transition-all border flex items-center gap-1.5',
                      isActive && pv
                        ? opt?.color
                        : isActive && !pv ? 'bg-gray-200 text-gray-700 border-gray-300'
                        : 'text-gray-400 border-gray-200 hover:bg-gray-50')}>
                    <span>{pv === 'reserve' ? '长期储备' : pv || '全部'}</span>
                    <span className={clsx('text-[10px] font-bold min-w-[18px] h-[18px] rounded-full flex items-center justify-center',
                      isActive ? 'bg-white/60 text-inherit' : 'bg-gray-100 text-gray-400')}>
                      {count}
                    </span>
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {/* Position cards */}
        {filtered.length === 0 ? (
          <div className="bg-white rounded-xl border border-gray-200 flex items-center justify-center h-32 text-sm text-gray-400">
            {search || priorityFilter ? '没有匹配的岗位' : '暂无岗位'}
          </div>
        ) : (
          <div className="space-y-2">
            {filtered.map(p => {
              const total = p.candidate_count || 0;
              const interviewCount = ((p.funnel || {})['interview_scheduled'] || 0) + ((p.funnel || {})['interview_completed'] || 0);
              const offerCount = (p.funnel || {})['offer'] || 0;
              const hiredCount = (p.funnel || {})['onboarded'] || 0;
              return (
                <div key={p.id} onClick={() => openDetail(p)}
                  className="bg-white rounded-xl border border-gray-200 px-5 py-4 hover:border-primary-300 hover:shadow-sm cursor-pointer transition-all group">
                  <div className="flex items-center gap-4">
                    {/* Left: name + meta */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-semibold text-gray-800 truncate">{p.name}</span>
                        {isHr ? (
                          <button onClick={e => { e.stopPropagation(); const opts = ['', 'P0', 'P1', 'P2', 'reserve']; const idx = opts.indexOf(p.priority || ''); const next = opts[(idx + 1) % opts.length]; updatePosition(p.id, { priority: next }).then(() => onUpdate()); }}
                            className={clsx('px-1.5 py-0.5 rounded text-[9px] font-bold border cursor-pointer hover:opacity-80', p.priority ? PRIORITY_OPTIONS.find(o => o.value === p.priority)?.color : 'border-dashed border-gray-300 text-gray-400')}>
                            {PRIORITY_OPTIONS.find(o => o.value === p.priority)?.label || p.priority || '设优先级'}
                          </button>
                        ) : p.priority ? (
                          <span className={clsx('px-1.5 py-0 rounded text-[9px] font-bold border', PRIORITY_OPTIONS.find(o => o.value === p.priority)?.color)}>
                            {PRIORITY_OPTIONS.find(o => o.value === p.priority)?.label || p.priority}
                          </span>
                        ) : null}
                        <span className={clsx('px-1.5 py-0.5 rounded text-[10px] font-medium', statusColors[p.status])}>
                          {statusLabels[p.status]}
                        </span>
                        {p.is_public ? (
                          <span className="px-1.5 py-0.5 rounded text-[10px] font-medium text-indigo-600 bg-indigo-50 border border-indigo-200 flex items-center gap-0.5"><Globe size={9} />公开</span>
                        ) : null}
                      </div>
                      <p className="text-xs text-gray-500 mt-1 truncate">
                        {p.department || '未分配部门'}
                        {p.background && <span className="ml-2 text-gray-400">· {p.background.slice(0, 40)}</span>}
                      </p>
                    </div>

                    {/* Right: funnel mini stats */}
                    <div className="flex items-center gap-4 text-xs shrink-0">
                      <div className="text-center">
                        <div className="font-bold text-gray-800">{total}</div>
                        <div className="text-gray-400">候选人</div>
                      </div>
                      <div className="text-center">
                        <div className="font-bold text-indigo-600">{interviewCount}</div>
                        <div className="text-gray-400">面试</div>
                      </div>
                      <div className="text-center">
                        <div className="font-bold text-green-600">{offerCount + hiredCount}</div>
                        <div className="text-gray-400">Offer</div>
                      </div>
                      <ChevronRight size={16} className="text-gray-300 group-hover:text-primary-500 transition-colors" />
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
